/**
 * 资金费率历史 API（funding-collector GET /api/funding-collector/funding-rates）
 */

import { API_CONFIG } from '@/config/api';
import { apiFetch } from '@/lib/apiFetch';

const BASE = API_CONFIG.FUNDING_RATES_PATH_PREFIX;
const PATH = '/funding-rates';

export interface FundingRateRecord {
  ts: string; // datetime from DB, e.g. "2024-01-01 00:00:00" or ISO
  exchange: string;
  inst_id: string;
  rate: number;
  funding_time: number;
}

export interface FundingRatesResponse {
  exchange: string;
  inst_id: string;
  start_time: string;
  end_time: string;
  count: number;
  data: FundingRateRecord[];
}

export interface FundingRatesParams {
  exchange: string;
  inst_id: string;
  start_time: string; // ISO 8601
  end_time: string;
  limit?: number | null;
  interval?: number | null;
}

export interface FundingRatesBatchParams {
  exchange: string;
  inst_ids: string[];
  start_time: string; // ISO 8601
  end_time: string;
  limit?: number | null;
}

export interface FundingRatesBatchResponse {
  exchange: string;
  inst_ids: string[];
  start_time: string;
  end_time: string;
  data: Record<string, FundingRateRecord[]>;
}

export async function getFundingRates(params: FundingRatesParams): Promise<FundingRatesResponse> {
  const q = new URLSearchParams();
  q.set('exchange', params.exchange);
  q.set('inst_id', params.inst_id);
  q.set('start_time', params.start_time);
  q.set('end_time', params.end_time);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.interval != null) q.set('interval', String(params.interval));

  const res = await apiFetch(`${BASE}${PATH}?${q.toString()}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<FundingRatesResponse>;
}

export async function getFundingRatesBatch(params: FundingRatesBatchParams): Promise<FundingRatesBatchResponse> {
  const res = await apiFetch(`${BASE}${PATH}/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json() as Promise<FundingRatesBatchResponse>;
}

/** Exchange names supported by funding-collector API */
export const FUNDING_EXCHANGES = [
  'Binance',
  'Bybit',
  'OKX',
  'Bitget',
  'Gateio',
  'Hyperliquid',
  'Paradex',
] as const;

export type FundingExchangeName = (typeof FUNDING_EXCHANGES)[number];
